import { OrganizationAddress } from '@/features/organization/api/OrganizationModel'
import { CardBody, InputGroup, useAddressLatinCharacterValidation } from '@/common'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { NON_EU_COUNTRIES } from '@/common'

export default function OrganizationAddressForm({
  initialState,
  onChange
}: {
  initialState: OrganizationAddress
  onChange: (address: OrganizationAddress) => void
}) {
  const { t } = useTranslation()
  const [state, setState] = useState(initialState)
  const { street, streetNumber, subDivision, country, postCode, poBox, city, street2 } = state
  const countryName = t(`countriesByIsoAlpha2:${country}`)

  // Use generalized validation hook for all address fields
  const errors = useAddressLatinCharacterValidation(
    {
      street,
      street2,
      streetNumber,
      postCode,
      poBox,
      city,
      subDivision
    },
    t('common:errors.latinCharacters')
  )

  const onInputChange = useCallback(
    (evt: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>) => {
      setState({ ...state, [evt.target.id]: evt.target.value })
      onChange({ ...state, [evt.target.id]: evt.target.value })
    },
    [state]
  )

  return (
    <div className="sm:flex sm:gap-2 sm:items-end">
      <div className="sm:w-3/5 mb-2 sm:mb-0">
        <CardBody className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <InputGroup
              id="street"
              label={t('common:inputFields.street.label')}
              value={street}
              onChange={onInputChange}
              error={errors.street || undefined}
            />

            <InputGroup
              id="streetNumber"
              label={t('common:inputFields.streetNumber.label')}
              value={streetNumber}
              onChange={onInputChange}
              error={errors.streetNumber || undefined}
            />
          </div>

          <InputGroup
            id="street2"
            label={t('common:inputFields.street2.label')}
            value={street2}
            onChange={onInputChange}
            error={errors.street2 || undefined}
          />

          <div className="grid md:grid-cols-3 gap-4">
            <InputGroup
              id="postCode"
              label={t('common:inputFields.postCode.label')}
              value={postCode}
              onChange={onInputChange}
              required={NON_EU_COUNTRIES.find(c => c.value === country)?.isPostCodeRequired}
              error={errors.postCode || undefined}
            />
            <InputGroup
              id="city"
              label={t('common:inputFields.city.label')}
              value={city}
              onChange={onInputChange}
              required
              error={errors.city || undefined}
            />
            <InputGroup
              id="poBox"
              label={t('common:inputFields.poBox.label')}
              value={poBox}
              onChange={onInputChange}
              error={errors.poBox || undefined}
            />
          </div>
          <InputGroup
            id="subDivision"
            label={t('common:inputFields.subDivision.label')}
            value={subDivision}
            onChange={onInputChange}
            error={errors.subDivision || undefined}
          />
          <InputGroup
            id="country"
            label={t('common:inputFields.country.label')}
            value={countryName}
            disabled
          />
        </CardBody>
      </div>
    </div>
  )
}
